import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

class CognacSizeSelector extends HTMLElement {
  #controller;
  #listeners;
  #confirmed = '';
  #pending = false;

  connectedCallback() {
    this.#listeners = new AbortController();
    const select = this.querySelector('select[data-size]');
    if (!select) return;
    select.value = '';
    this.#clearStickySize();
    const system = this.querySelector('[data-size-system]');
    const fit = this.querySelector('[data-size-fit]');
    if (system) system.value = '';
    if (fit?.querySelector('option[value=""]')) fit.value = '';
    for (const field of [system, fit]) {
      field?.addEventListener('change', this.#filter, { signal: this.#listeners.signal });
    }
    const input = this.closest('form')?.querySelector('input[name="id"]');
    if (input) input.value = '';
    select.addEventListener('change', this.#change, { signal: this.#listeners.signal });
    select.addEventListener('invalid', () => {
      this.#message(this.dataset.requiredMessage);
      select.setAttribute('aria-invalid', 'true');
    }, { signal: this.#listeners.signal });
    this.closest('form')?.addEventListener('submit', (event) => {
      if (!this.validate()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, { capture: true, signal: this.#listeners.signal });
    this.#disableButtons();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#listeners?.abort();
  }

  #filter = () => {
    this.#controller?.abort();
    this.#confirmed = '';
    this.#pending = false;
    const input = this.closest('form')?.querySelector('input[name="id"]');
    if (input) input.value = '';
    const select = this.querySelector('select[data-size]');
    const system = this.querySelector('[data-size-system]')?.value;
    const fit = this.querySelector('[data-size-fit]')?.value;
    if (!select) return;
    const placeholder = select.options[0].cloneNode(true);
    select.replaceChildren(placeholder);
    this.querySelector('template[data-cognac-sizes]')?.content.querySelectorAll('option').forEach(option => {
      if (system && fit && option.dataset.system === system && option.dataset.fit === fit) {
        select.append(option.cloneNode(true));
      }
    });
    select.value = '';
    select.disabled = !system || !fit || select.options.length === 1;
    select.removeAttribute('aria-invalid');
    this.#disableButtons();
    this.#message(this.dataset.hasSizes === 'true' ? this.dataset.requiredMessage : this.dataset.pendingMessage);
  };

  #message(text) {
    const message = this.querySelector('[role="status"]');
    if (message) message.textContent = text || '';
  }

  #clearStickySize() {
    const sticky = this.closest('.shopify-section')?.querySelector(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"]`);
    const label = sticky?.querySelector('.sticky-add-to-cart__variant');
    if (label) label.textContent = this.querySelector('select[data-size] option')?.textContent || '';
    if (sticky) sticky.dataset.currentVariantId = '';
  }

  #disableButtons() {
    this.#clearStickySize();
    const form = this.closest('product-form-component');
    form?.querySelectorAll('[ref="addToCartButton"]').forEach(button => { button.disabled = true; });
    this.closest('.shopify-section')?.querySelectorAll(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`).forEach(button => { button.disabled = true; });
  }

  validate() {
    const select = this.querySelector('select[data-size]');
    const input = this.closest('form')?.querySelector('input[name="id"]');
    const system = this.querySelector('[data-size-system]');
    const fit = this.querySelector('[data-size-fit]');
    const valid = !!system?.value && !!fit?.value && !!select && !select.disabled && !this.#pending && !!this.#confirmed &&
      select.value === this.#confirmed && input?.value === this.#confirmed &&
      select.selectedOptions.length === 1 && !select.selectedOptions[0].disabled &&
      select.selectedOptions[0].dataset.system === system.value && select.selectedOptions[0].dataset.fit === fit.value;
    if (!valid) {
      if (this.dataset.hasSizes === 'true') {
        this.#message(this.#pending ? this.dataset.loadingMessage : this.dataset.requiredMessage);
      }
      select?.setAttribute('aria-invalid', 'true');
      this.scrollIntoView({ block: 'center' });
      const target = !system?.value ? system : !fit?.value ? fit : select;
      target?.focus({ preventScroll: true });
    }
    return valid;
  }

  #change = async () => {
    const select = this.querySelector('select[data-size]');
    const input = this.closest('form')?.querySelector('input[name="id"]');
    const value = select?.value;
    this.#controller?.abort();
    this.#confirmed = '';
    this.#pending = false;
    if (input) input.value = '';
    this.#disableButtons();
    if (!value || select.disabled || select.selectedOptions[0]?.disabled) {
      this.#message(this.dataset.requiredMessage);
      return;
    }
    this.#pending = true;
    this.#message(this.dataset.loadingMessage);
    select.removeAttribute('aria-invalid');
    const controller = new AbortController();
    this.#controller = controller;
    this.dispatchEvent(new VariantSelectedEvent({ id: value }));
    try {
      const url = new URL(this.dataset.productUrl, window.location.origin);
      url.searchParams.set('variant', value);
      url.searchParams.set('section_id', this.dataset.sectionId);
      const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (previewId) url.searchParams.set('preview_theme_id', previewId);
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error('Size lookup failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (controller.signal.aborted || this.querySelector('select[data-size]')?.value !== value) return;
      const selector = html.querySelector(`cognac-size-selector[data-product-id="${this.dataset.productId}"]`);
      const json = selector?.querySelector('[data-cognac-selected-variant]')?.textContent;
      const variant = json ? JSON.parse(json) : null;
      if (!variant || String(variant.id) !== value || !variant.available || variant.title === 'Default Title') {
        throw new Error('Size unavailable');
      }
      this.dispatchEvent(new VariantUpdateEvent(variant, value, { html, productId: this.dataset.productId }));
      if (input) input.value = value;
      this.#confirmed = value;
      this.#pending = false;
      this.closest('product-form-component')?.querySelectorAll('[ref="addToCartButton"]').forEach(button => { button.disabled = false; });
      this.closest('.shopify-section')?.querySelectorAll(`sticky-add-to-cart[data-product-id="${this.dataset.productId}"] [ref="addToCartButton"]`).forEach(button => { button.disabled = false; });
      this.#message('');
      select.removeAttribute('aria-invalid');
    } catch (error) {
      if (controller.signal.aborted) return;
      this.#pending = false;
      this.#confirmed = '';
      if (input) input.value = '';
      select.value = '';
      select.setAttribute('aria-invalid', 'true');
      this.#message(this.dataset.errorMessage);
      this.#disableButtons();
    }
  };
}

if (!customElements.get('cognac-size-selector')) {
  customElements.define('cognac-size-selector', CognacSizeSelector);
}
